import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../utils/api";
import { toast } from "react-toastify";
import {
	formatJapaneseDateWithWeekday,
	formatJapaneseTime,
} from "../utils/dateUtils";
import { IconType } from "react-icons";
import {
	SOCIAL_PLATFORM_ICONS,
	SOCIAL_PLATFORM_MAP,
	SocialPlatformKey,
} from "../constants/socialPlatforms";
import { usePageTitle } from "../hooks/usePageTitle";

interface Event {
	id: number;
	external_id: string;
	title: string;
	slug: string;
	date: string;
	time: string;
	venue_name: string;
	prefecture: string;
	city: string;
	image_url: string;
	organizer_id: number;
}

interface OrganizerSocialLink {
	platform: string;
	url: string;
}

interface Organizer {
	id: number;
	external_id: string;
	organizer_name: string;
	organizer_type: string;
	description: string;
	logo_url: string;
	website: string;
	is_verified: boolean;
	social_links?: OrganizerSocialLink[];
	events: Event[];
}

const OrganizerProfilePage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [organizer, setOrganizer] = useState<Organizer | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	usePageTitle(organizer?.organizer_name, { fallback: "主催者プロフィール" });

	// 主催者データの取得
	useEffect(() => {
		const fetchOrganizerData = async () => {
			if (!id) {
				setError("主催者IDが指定されていません");
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				const response = await apiClient.get(`/organizers/${id}`);

				if (response.data && response.data.organizer) {
					setOrganizer(response.data.organizer);
				} else {
					throw new Error("主催者データが見つかりませんでした");
				}

				setIsLoading(false);
			} catch (error) {
				console.error("主催者データの取得に失敗しました:", error);
				setError("主催者データの取得に失敗しました");
				setIsLoading(false);
				toast.error("主催者データの取得に失敗しました");
			}
		};

		fetchOrganizerData();
	}, [id]);

	// ローディング表示
	if (isLoading) {
		return (
			<div className="max-w-4xl mx-auto py-10 text-center">
				<p className="text-gray-600 dark:text-gray-300">
					主催者情報を読み込み中...
				</p>
			</div>
		);
	}

	// エラー表示
	if (error || !organizer) {
		return (
			<div className="max-w-4xl mx-auto py-10 text-center">
				<p className="text-red-600 dark:text-red-400">
					{error || "主催者が見つかりませんでした"}
				</p>
				<button
					onClick={() => navigate("/")}
					className="mt-4 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded transition-all"
				>
					トップページに戻る
				</button>
			</div>
		);
	}

	const organizerTypeLabel =
		{
			artist: "アーティスト",
			company: "企業",
			individual: "個人",
		}[organizer.organizer_type] || organizer.organizer_type;

	const socialLinkBadges = (organizer.social_links || [])
		.map((link) => {
			const key = link.platform as SocialPlatformKey;
			const meta = SOCIAL_PLATFORM_MAP[key];
			const Icon = SOCIAL_PLATFORM_ICONS[key];
			if (!meta || !Icon || !link.url) {
				return null;
			}
			return {
				key,
				url: link.url,
				label: meta.label,
				Icon,
			};
		})
		.filter(
			(
				link
			): link is {
				key: SocialPlatformKey;
				url: string;
				label: string;
				Icon: IconType;
			} => link !== null
		);

	return (
		<div className="max-w-6xl mx-auto space-y-6 pb-10">
			{/* Back Button */}
			<div className="pt-6">
				<button
					onClick={() => navigate(-1)}
					className="flex items-center text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors"
				>
					<svg
						className="w-5 h-5 mr-1"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					戻る
				</button>
			</div>

			{/* Organizer Header */}
			<section className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/5 text-white">
				<div className="absolute inset-0">
					{organizer.logo_url ? (
						<img
							src={organizer.logo_url}
							alt={`${organizer.organizer_name} ロゴ`}
							className="w-full h-full object-cover blur-3xl scale-110"
						/>
					) : (
						<div className="w-full h-full bg-gradient-to-br from-slate-700 via-gray-800 to-black" />
					)}
				</div>
				<div className="absolute inset-0 bg-black/70" />
				<div className="relative flex flex-col lg:flex-row">
					<div className="flex-1 p-6 sm:p-10 space-y-6">
						<div className="space-y-2">
							<p className="text-xs uppercase tracking-[0.35em] text-white/60">
								Organizer Profile
							</p>
							<h1 className="text-3xl sm:text-4xl font-bold leading-tight">
								{organizer.organizer_name}
							</h1>
						</div>
						<div className="space-y-4 text-base text-white/90">
							<div className="flex items-start gap-3">
								<svg
									className="w-6 h-6 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
									/>
								</svg>
								<div>
									<div className="text-lg font-semibold">
										{organizerTypeLabel}
									</div>
								</div>
							</div>
							{organizer.website && (
								<div className="flex items-start gap-3">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
										/>
									</svg>
									<div>
										<a
											href={organizer.website}
											target="_blank"
											rel="noopener noreferrer"
											className="text-lg font-semibold hover:text-blue-300 transition-colors"
										>
											ウェブサイト
										</a>
									</div>
								</div>
							)}
							{socialLinkBadges.length > 0 && (
								<div className="flex flex-wrap gap-3">
									{socialLinkBadges.map(({ key, url, label, Icon }) => (
										<a
											key={key}
											href={url}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
										>
											<Icon className="w-4 h-4" />
											<span>{label}</span>
										</a>
									))}
								</div>
							)}
						</div>
						<div className="flex flex-wrap gap-3 text-sm text-white/80">
							{organizer.is_verified && (
								<span className="px-3 py-1 rounded-full border border-white/40 flex items-center gap-2">
									<svg
										className="w-4 h-4 text-blue-400"
										fill="currentColor"
										viewBox="0 0 20 20"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											fillRule="evenodd"
											d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clipRule="evenodd"
										/>
									</svg>
									認証済み
								</span>
							)}
						</div>
					</div>
					{organizer.logo_url && (
						<div className="w-full lg:w-[360px] p-6 sm:p-10 pt-0 lg:pt-10">
							<div className="bg-white/5 rounded-2xl border border-white/15 shadow-2xl overflow-hidden">
								<div className="aspect-square">
									<img
										src={organizer.logo_url}
										alt={`${organizer.organizer_name} ロゴ`}
										className="object-cover w-full h-full"
									/>
								</div>
							</div>
						</div>
					)}
				</div>
			</section>

			{/* Organizer Details */}
			{organizer.description && (
				<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
					<div className="px-6 pt-6 pb-2">
						<h2 className="text-xl font-bold dark:text-white">
							主催者について
						</h2>
					</div>
					<div className="p-6">
						<p className="whitespace-pre-line text-gray-700 dark:text-gray-300 leading-relaxed">
							{organizer.description}
						</p>
					</div>
				</section>
			)}

			{/* Events List */}
			<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
				<div className="p-6 border-b dark:border-gray-700">
					<h2 className="text-xl font-bold dark:text-white">
						開催予定のイベント
					</h2>
				</div>

				{organizer.events.length === 0 ? (
					<div className="text-center py-10 text-gray-500 dark:text-gray-400">
						<svg
							className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							/>
						</svg>
						<p>現在開催予定のイベントはありません</p>
					</div>
				) : (
					<div className="p-6">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{organizer.events.map((event) => {
								const formattedDate = formatJapaneseDateWithWeekday(event.date);
								const formattedTime = formatJapaneseTime(event.time);

								return (
									<div
										key={event.id}
										onClick={() =>
											navigate(`/events/${event.slug || event.external_id}`)
										}
										className="bg-gray-50 dark:bg-gray-700 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all border border-gray-100 dark:border-gray-600"
									>
										<div className="aspect-video overflow-hidden">
											<img
												src={
													event.image_url ||
													"https://via.placeholder.com/400x225"
												}
												alt={event.title}
												className="w-full h-full object-cover"
											/>
										</div>
										<div className="p-4">
											<h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2 line-clamp-2">
												{event.title}
											</h3>
											<div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
												<div className="flex items-center gap-2">
													<svg
														className="w-4 h-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
														/>
													</svg>
													<span>{formattedDate}</span>
												</div>
												{formattedTime && (
													<div className="flex items-center gap-2">
														<svg
															className="w-4 h-4"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
															/>
														</svg>
														<span>{formattedTime}</span>
													</div>
												)}
												<div className="flex items-center gap-2">
													<svg
														className="w-4 h-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
														/>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
														/>
													</svg>
													<span>
														{event.venue_name ||
															`${event.prefecture} ${event.city}`.trim()}
													</span>
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</section>
		</div>
	);
};

export default OrganizerProfilePage;
